import { useEffect, useState, type KeyboardEvent } from 'react';
import type { Grade, LeadRulesDto } from '@coachrex/shared';
import { useLeadScoring, useSaveLeadScoring } from '../lib/api';
import { ErrorBox, GradeBadge, PageHeader, Pills, Skeleton } from '../components/ui';
import './scoring.css';

type ListKey = 'mustHaves' | 'disqualifiers' | 'preferred' | 'objections';

const CARDS: { key: ListKey; title: string; help: string; placeholder: string }[] = [
  { key: 'mustHaves', title: 'Must-haves', help: 'All of these are needed for an A.', placeholder: 'e.g. Owns the property' },
  { key: 'disqualifiers', title: 'Disqualifiers', help: 'Any one of these makes the lead a D.', placeholder: 'e.g. Renter or not the owner' },
  { key: 'preferred', title: 'Preferred signals', help: 'Not required. Each one raises the lead within its grade.', placeholder: 'e.g. Already has plans or permits' },
  { key: 'objections', title: 'Objections to track', help: 'The AI tags every call, text and email where one of these comes up.', placeholder: 'e.g. Price is too high' },
];
const GRADES: Grade[] = ['A', 'B', 'C', 'D'];

/** Handoff screen 09: how the AI grades leads A–D. */
export function LeadScoring() {
  const { data, error, isLoading } = useLeadScoring();
  const save = useSaveLeadScoring();
  const [draft, setDraft] = useState<LeadRulesDto | null>(null);
  const [dirty, setDirty] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (data && !dirty) {
      const { version: _v, updatedAt: _u, ...rules } = data.rules;
      setDraft(rules);
    }
  }, [data, dirty]);

  if (error) return <div className="page"><ErrorBox error={error} /></div>;
  if (isLoading || !draft || !data) {
    return (
      <div className="page">
        <Skeleton height={60} />
        <Skeleton height={160} />
        <Skeleton height={240} />
      </div>
    );
  }

  // Any manual edit to the brief or rules makes it a custom setup; objections don't count.
  const edit = (patch: Partial<LeadRulesDto>, custom = true) => {
    setDraft({ ...draft, ...patch, ...(custom ? { template: 'Custom' } : {}) });
    setDirty(true);
    setSaved(false);
  };
  const pickTemplate = (name: string) => {
    const t = data.templates[name];
    if (!t) return edit({ template: 'Custom' }, false);
    edit({ template: name, prompt: t.prompt, mustHaves: t.mustHaves, disqualifiers: t.disqualifiers, preferred: t.preferred }, false);
  };
  const options = [...Object.keys(data.templates), 'Custom'].map((v) => ({ value: v, label: v }));
  const canSave = dirty && draft.prompt.trim() && !save.isPending;

  return (
    <div className="page">
      <PageHeader
        title="Lead scoring"
        subtitle={`Tell Coach Lex what a good lead looks like. The AI reads every call, text and email against these rules and grades each lead A to D. Rules version ${data.rules.version}.`}
      />

      <section className="card" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div className="form-label">
          <span>Start from a template</span>
          <Pills options={options} value={draft.template} onChange={pickTemplate} />
        </div>
        <label className="form-label">
          <span>Describe a good lead</span>
          <textarea className="text-input" rows={4} value={draft.prompt} onChange={(e) => edit({ prompt: e.target.value })} placeholder="Who buys from you, how soon, and how they pay." />
        </label>
        <span className="footnote-text">Picking a template replaces the description and rules. Objections to track are kept.</span>
      </section>

      <div className="rule-grid">
        {CARDS.map((c) => (
          <RuleCard key={c.key} title={c.title} help={c.help} placeholder={c.placeholder} items={draft[c.key]} onChange={(items) => edit({ [c.key]: items }, c.key !== 'objections')} />
        ))}
      </div>

      <section className="card" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <span className="card-title">Grades</span>
        <span className="rule-card__help">What each grade means to your team. Shown wherever a grade appears.</span>
        <div className="grade-rows">
          {GRADES.map((g) => (
            <label key={g} className="grade-row">
              <GradeBadge grade={g} size={26} />
              <input
                className="text-input"
                value={draft.gradeMeanings[g]}
                aria-label={`Meaning of grade ${g}`}
                onChange={(e) => edit({ gradeMeanings: { ...draft.gradeMeanings, [g]: e.target.value } }, false)}
              />
            </label>
          ))}
        </div>
      </section>

      <div className="save-bar">
        {save.error && <ErrorBox error={save.error} />}
        <span className="footnote-text">
          {saved ? 'Saved. New grades use these rules; existing grades update as leads get new activity.' : dirty ? 'Unsaved changes.' : 'Leads are re-graded when they reply or after a scored call.'}
        </span>
        {dirty && (
          <button className="btn btn--secondary" onClick={() => { setDirty(false); setSaved(false); }}>
            Discard
          </button>
        )}
        <button
          className="btn btn--accent"
          disabled={!canSave}
          onClick={() =>
            save.mutate(
              { ...draft, prompt: draft.prompt.trim() },
              { onSuccess: () => { setDirty(false); setSaved(true); } },
            )
          }
        >
          {save.isPending ? 'Saving…' : 'Save rules'}
        </button>
      </div>
    </div>
  );
}

function RuleCard({ title, help, placeholder, items, onChange }: { title: string; help: string; placeholder: string; items: string[]; onChange: (items: string[]) => void }) {
  const [text, setText] = useState('');
  const add = () => {
    const v = text.trim();
    if (!v) return;
    if (!items.some((i) => i.toLowerCase() === v.toLowerCase())) onChange([...items, v]);
    setText('');
  };
  const onKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      add();
    }
  };
  return (
    <section className="card rule-card">
      <span className="card-title">{title}</span>
      <span className="rule-card__help">{help}</span>
      {items.length ? (
        <ul className="rule-list">
          {items.map((i) => (
            <li key={i}>
              <span>{i}</span>
              <button aria-label={`Remove ${i}`} onClick={() => onChange(items.filter((x) => x !== i))}>
                ×
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <span className="footnote-text">None yet.</span>
      )}
      <div className="rule-add">
        <input className="text-input" value={text} onChange={(e) => setText(e.target.value)} onKeyDown={onKey} placeholder={placeholder} aria-label={`Add to ${title}`} />
        <button className="btn btn--secondary" disabled={!text.trim()} onClick={add}>
          Add
        </button>
      </div>
    </section>
  );
}
